import { useState, useEffect } from "react";
import { accountingAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
    TrendingUp, 
    TrendingDown, 
    Wallet, 
    ArrowUpRight, 
    ArrowDownRight,
    Receipt,
    Loader2,
    AlertCircle,
    CreditCard
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatDate } from "@/lib/utils";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const EXPENSE_CATEGORIES = [
    "Rent", "Supplies", "Staff Payment", "Utilities", 
    "Marketing", "Equipment", "Maintenance", "Other"
];

const INCOME_CATEGORIES = [
    "Service", "Product Sale", "Booking Fee", "Subscription", "Other"
];

const PAYMENT_METHODS = ["cash", "mobile_money", "card", "bank_transfer"];

export default function AccountingPage() {
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState({
        income: 0,
        expenses: 0,
        booking_fees: 0,
        net: 0,
        period: null,
        appointments: null
    });
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState("month");
    const [showIncomeDialog, setShowIncomeDialog] = useState(false);
    const [showExpenseDialog, setShowExpenseDialog] = useState(false);
    const [error, setError] = useState(null);
    
    const [incomeForm, setIncomeForm] = useState({
        amount: "",
        description: "",
        category: "Service",
        date: new Date().toISOString().split('T')[0]
    });
    
    const [expenseForm, setExpenseForm] = useState({
        amount: "",
        description: "",
        category: "Supplies",
        date: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        loadData();
    }, [period]);

    const getDateRange = () => {
        const today = new Date();
        let start_date, end_date;
        
        end_date = today.toISOString().split('T')[0];
        
        switch(period) {
            case 'day':
                start_date = end_date;
                break;
            case 'week':
                const weekStart = new Date(today);
                weekStart.setDate(today.getDate() - today.getDay());
                start_date = weekStart.toISOString().split('T')[0];
                break;
            case 'month':
                start_date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
                break;
            case 'year':
                start_date = `${today.getFullYear()}-01-01`;
                break;
            default:
                start_date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
        }
        
        return { start_date, end_date };
    };

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const { start_date, end_date } = getDateRange();
            
            const [txnResponse, summaryResponse] = await Promise.all([
                accountingAPI.getTransactions({ start_date, end_date }),
                accountingAPI.getSummary({ start_date, end_date })
            ]);
            
            const transactionsData = txnResponse?.data?.transactions || [];
            const summaryData = summaryResponse?.data?.summary || {};
            const totals = summaryData.totals || {};
            
            const finalSummary = {
                income: totals.income || 0,
                expenses: totals.expenses || 0,
                booking_fees: totals.booking_fees || 0,
                net: totals.net || 0,
                period: summaryData.period || null,
                appointments: summaryData.appointments || null
            };
            
            setTransactions(transactionsData);
            setSummary(finalSummary);
            
        } catch (error) {
            setError("Failed to load accounting data");
            toast.error("Failed to load accounting data");
            setTransactions([]);
            setSummary({ income: 0, expenses: 0, booking_fees: 0, net: 0, period: null, appointments: null });
        } finally {
            setLoading(false);
        }
    };

    const handleAddIncome = async () => {
        if (!incomeForm.amount || !incomeForm.description) {
            toast.error("Amount and description are required");
            return;
        }
        
        try {
            await accountingAPI.createIncome({
                type: 'income',
                amount: parseFloat(incomeForm.amount),
                description: incomeForm.description,
                category: incomeForm.category,
                date: incomeForm.date
            });
            toast.success("Income recorded!");
            setShowIncomeDialog(false);
            setIncomeForm({
                amount: "",
                description: "",
                category: "Service",
                date: new Date().toISOString().split('T')[0]
            });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to record income");
        }
    };

    const handleAddExpense = async () => {
        if (!expenseForm.amount || !expenseForm.description || !expenseForm.category) {
            toast.error("Amount, description and category are required");
            return;
        }
        
        try {
            await accountingAPI.createExpense({
                type: 'expense',
                amount: parseFloat(expenseForm.amount),
                description: expenseForm.description,
                category: expenseForm.category,
                date: expenseForm.date
            });
            toast.success("Expense recorded!");
            setShowExpenseDialog(false);
            setExpenseForm({
                amount: "",
                description: "",
                category: "Supplies",
                date: new Date().toISOString().split('T')[0]
            });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to record expense");
        }
    };

    // All transactions filtered
    const safeTransactions = Array.isArray(transactions) ? transactions : [];
    
    // Income includes both 'income' and 'booking_fee' types
    const allIncomeTransactions = safeTransactions.filter(t => t.type === 'income' || t.type === 'booking_fee');
    const serviceIncomeTransactions = safeTransactions.filter(t => t.type === 'income');
    const bookingFeeTransactions = safeTransactions.filter(t => t.type === 'booking_fee');
    const expenseTransactions = safeTransactions.filter(t => t.type === 'expense');

    // Calculate totals - booking fees are income
    const totalIncome = summary.income + summary.booking_fees;
    const totalExpenses = summary.expenses;
    const netProfit = summary.net;

    // Build income chart data - group by category, treat booking_fee as "Booking Fee" category
    const incomeByCategory = {};
    allIncomeTransactions.forEach(t => {
        const category = t.type === 'booking_fee' ? 'Booking Fee' : (t.category || 'Service');
        const amount = parseFloat(t.amount) || 0;
        incomeByCategory[category] = (incomeByCategory[category] || 0) + amount;
    });
    
    const incomeChartData = Object.entries(incomeByCategory).map(([name, value]) => ({
        name,
        value
    }));

    // Build expense chart data
    const expenseByCategory = {};
    expenseTransactions.forEach(t => {
        const category = t.category || 'Other';
        const amount = parseFloat(t.amount) || 0;
        expenseByCategory[category] = (expenseByCategory[category] || 0) + amount;
    });
    
    const expenseChartData = Object.entries(expenseByCategory).map(([name, value]) => ({
        name,
        value
    }));

    const INCOME_COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899', '#06B6D4'];
    const EXPENSE_COLORS = ['#EF4444', '#F97316', '#DC2626', '#B91C1C', '#991B1B', '#7F1D1D'];

    return (
        <div className="p-4 md:p-8 lg:p-12 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Accounting</h1>
                    <p className="text-muted-foreground">Track your income and expenses</p>
                </div>
                <div className="flex items-center gap-3">
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-[130px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="day">Today</SelectItem>
                            <SelectItem value="week">This Week</SelectItem>
                            <SelectItem value="month">This Month</SelectItem>
                            <SelectItem value="year">This Year</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button 
                        variant="outline" 
                        onClick={() => setShowExpenseDialog(true)}
                        className="text-destructive border-destructive/30"
                    >
                        <ArrowDownRight className="w-4 h-4 mr-2" />
                        Expense
                    </Button>
                    <Button onClick={() => setShowIncomeDialog(true)} className="rounded-full">
                        <ArrowUpRight className="w-4 h-4 mr-2" />
                        Income
                    </Button>
                </div>
            </div>

            {/* Error State */}
            {error && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-destructive">
                        <AlertCircle className="w-5 h-5" />
                        <span className="font-medium">{error}</span>
                    </div>
                    <Button variant="outline" size="sm" className="mt-3" onClick={loadData}>
                        Try Again
                    </Button>
                </div>
            )}

            {/* Loading State */}
            {loading ? (
                <div className="flex flex-col items-center justify-center h-64 space-y-4">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <p className="text-muted-foreground">Loading accounting data...</p>
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="grid md:grid-cols-4 gap-4">
                        {/* Total Income Card - includes service income + booking fees */}
                        <Card className="border-border/40 shadow-card">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Total Income</p>
                                        <p className="text-3xl font-accent font-bold text-green-600 mt-1">
                                            {formatCurrency(totalIncome)}
                                        </p>
                                        <div className="space-y-1 mt-2">
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="text-muted-foreground">Services:</span>
                                                <span className="font-medium text-green-600">{formatCurrency(summary.income)}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="text-muted-foreground flex items-center gap-1">
                                                    <CreditCard className="w-3 h-3" />
                                                    Booking Fees:
                                                </span>
                                                <span className="font-medium text-green-600">{formatCurrency(summary.booking_fees)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                                        <TrendingUp className="w-6 h-6 text-green-600" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Total Expenses Card */}
                        <Card className="border-border/40 shadow-card">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Total Expenses</p>
                                        <p className="text-3xl font-accent font-bold text-red-600 mt-1">
                                            {formatCurrency(totalExpenses)}
                                        </p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <Badge variant="outline" className="text-xs">
                                                {expenseTransactions.length} transactions
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                                        <TrendingDown className="w-6 h-6 text-red-600" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Net Profit Card */}
                        <Card className="border-border/40 shadow-card">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Net Profit</p>
                                        <p className={`text-3xl font-accent font-bold mt-1 ${netProfit >= 0 ? 'text-primary' : 'text-red-600'}`}>
                                            {formatCurrency(netProfit)}
                                        </p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <Badge variant={netProfit >= 0 ? "default" : "destructive"} className="text-xs">
                                                {netProfit >= 0 ? 'Profitable' : 'Loss'}
                                            </Badge>
                                            <span className="text-xs text-muted-foreground">
                                                Margin: {(totalIncome > 0 ? (netProfit / totalIncome * 100).toFixed(1) : '0')}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                                        <Wallet className="w-6 h-6 text-primary" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Transaction Summary Card */}
                        <Card className="border-border/40 shadow-card">
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Transactions</p>
                                        <div className="space-y-2 mt-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-muted-foreground">Total:</span>
                                                <span className="text-sm font-medium">{safeTransactions.length}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-muted-foreground">Income:</span>
                                                <span className="text-sm font-medium text-green-600">{allIncomeTransactions.length}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-muted-foreground">Expenses:</span>
                                                <span className="text-sm font-medium text-red-600">{expenseTransactions.length}</span>
                                            </div>
                                            {summary.appointments && (
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-muted-foreground">Appointments:</span>
                                                    <span className="text-sm font-medium">{summary.appointments.total}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                                        <Receipt className="w-6 h-6 text-muted-foreground" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Charts */}
                    <div className="grid lg:grid-cols-2 gap-6">
                        {/* Income Breakdown - includes booking fees */}
                        <Card className="border-border/40 shadow-card">
                            <CardHeader>
                                <CardTitle className="font-heading text-lg flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-green-600" />
                                    Income Breakdown
                                </CardTitle>
                                <CardDescription>
                                    Total: {formatCurrency(totalIncome)} ({allIncomeTransactions.length} transactions)
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {incomeChartData.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>No income recorded for this period</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="h-[200px]">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={incomeChartData}
                                                        cx="50%"
                                                        cy="50%"
                                                        outerRadius={70}
                                                        dataKey="value"
                                                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                                                    >
                                                        {incomeChartData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={INCOME_COLORS[index % INCOME_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        {/* Legend */}
                                        <div className="grid grid-cols-2 gap-2 mt-4">
                                            {incomeChartData.map((item, index) => (
                                                <div key={item.name} className="flex items-center gap-2 text-sm">
                                                    <div 
                                                        className="w-3 h-3 rounded-full" 
                                                        style={{ backgroundColor: INCOME_COLORS[index % INCOME_COLORS.length] }}
                                                    />
                                                    <span className="text-muted-foreground truncate">{item.name}:</span>
                                                    <span className="font-medium">{formatCurrency(item.value)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>

                        {/* Expense Breakdown */}
                        <Card className="border-border/40 shadow-card">
                            <CardHeader>
                                <CardTitle className="font-heading text-lg flex items-center gap-2">
                                    <TrendingDown className="w-5 h-5 text-red-600" />
                                    Expense Breakdown
                                </CardTitle>
                                <CardDescription>
                                    Total: {formatCurrency(totalExpenses)} ({expenseTransactions.length} transactions)
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {expenseChartData.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>No expenses recorded for this period</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="h-[200px]">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={expenseChartData}
                                                        cx="50%"
                                                        cy="50%"
                                                        outerRadius={70}
                                                        dataKey="value"
                                                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                                                    >
                                                        {expenseChartData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        {/* Legend */}
                                        <div className="grid grid-cols-2 gap-2 mt-4">
                                            {expenseChartData.map((item, index) => (
                                                <div key={item.name} className="flex items-center gap-2 text-sm">
                                                    <div 
                                                        className="w-3 h-3 rounded-full" 
                                                        style={{ backgroundColor: EXPENSE_COLORS[index % EXPENSE_COLORS.length] }}
                                                    />
                                                    <span className="text-muted-foreground truncate">{item.name}:</span>
                                                    <span className="font-medium">{formatCurrency(item.value)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Transactions List */}
                    <Card className="border-border/40 shadow-card">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="font-heading text-lg">Recent Transactions</CardTitle>
                                <CardDescription>{safeTransactions.length} transactions total</CardDescription>
                            </div>
                            <Button variant="ghost" size="sm" onClick={loadData}>Refresh</Button>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="all" className="w-full">
                                <TabsList className="grid w-full grid-cols-3">
                                    <TabsTrigger value="all">All ({safeTransactions.length})</TabsTrigger>
                                    <TabsTrigger value="income">
                                        Income ({allIncomeTransactions.length})
                                    </TabsTrigger>
                                    <TabsTrigger value="expense">Expenses ({expenseTransactions.length})</TabsTrigger>
                                </TabsList>
                                <TabsContent value="all" className="mt-4">
                                    <TransactionList transactions={safeTransactions.slice(0, 20)} />
                                </TabsContent>
                                <TabsContent value="income" className="mt-4">
                                    <TransactionList transactions={allIncomeTransactions.slice(0, 20)} />
                                </TabsContent>
                                <TabsContent value="expense" className="mt-4">
                                    <TransactionList transactions={expenseTransactions.slice(0, 20)} />
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>
                </>
            )}

            {/* Add Income Dialog */}
            <Dialog open={showIncomeDialog} onOpenChange={setShowIncomeDialog}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Record Income</DialogTitle>
                        <DialogDescription>Add a new income entry</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Amount (GHS) *</Label>
                            <Input
                                type="number"
                                value={incomeForm.amount}
                                onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Description *</Label>
                            <Input
                                value={incomeForm.description}
                                onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })}
                                placeholder="e.g., Haircut service"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Category</Label>
                            <Select value={incomeForm.category} onValueChange={(v) => setIncomeForm({ ...incomeForm, category: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {INCOME_CATEGORIES.map((c) => (
                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Date</Label>
                            <Input
                                type="date"
                                value={incomeForm.date}
                                onChange={(e) => setIncomeForm({ ...incomeForm, date: e.target.value })}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setShowIncomeDialog(false)}>Cancel</Button>
                            <Button onClick={handleAddIncome}>Save Income</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Expense Dialog */}
            <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Record Expense</DialogTitle>
                        <DialogDescription>Add a new expense entry</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Amount (GHS) *</Label>
                            <Input
                                type="number"
                                value={expenseForm.amount}
                                onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                                placeholder="0.00"
                                min="0"
                                step="0.01"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Description *</Label>
                            <Input
                                value={expenseForm.description}
                                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                                placeholder="e.g., Monthly rent"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Category *</Label>
                            <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                                <SelectContent>
                                    {EXPENSE_CATEGORIES.map((c) => (
                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Date</Label>
                            <Input
                                type="date"
                                value={expenseForm.date}
                                onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setShowExpenseDialog(false)}>Cancel</Button>
                            <Button onClick={handleAddExpense}>Save Expense</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function TransactionList({ transactions }) {
    if (!transactions || !Array.isArray(transactions) || transactions.length === 0) {
        return (
            <div className="text-center py-12 text-muted-foreground">
                <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No transactions to show</p>
            </div>
        );
    }

    return (
        <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
            {transactions.map((txn, index) => {
                // Both 'income' and 'booking_fee' are treated as income
                const isIncome = txn.type === 'income' || txn.type === 'booking_fee';
                const isBookingFee = txn.type === 'booking_fee';
                
                return (
                    <div 
                        key={txn.id || index}
                        className="flex items-center justify-between p-4 rounded-xl bg-secondary/50 hover:bg-secondary/70 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isIncome ? 'bg-green-100' : 'bg-red-100'
                            }`}>
                                {isIncome ? (
                                    isBookingFee ? (
                                        <CreditCard className="w-5 h-5 text-green-600" />
                                    ) : (
                                        <ArrowUpRight className="w-5 h-5 text-green-600" />
                                    )
                                ) : (
                                    <ArrowDownRight className="w-5 h-5 text-red-600" />
                                )}
                            </div>
                            <div>
                                <p className="font-medium">{txn.description || 'No description'}</p>
                                <div className="flex items-center gap-2 mt-1">
                                    <Badge 
                                        variant={isIncome ? 'default' : 'destructive'} 
                                        className={`text-xs ${isIncome ? 'bg-green-600' : ''}`}
                                    >
                                        {isBookingFee ? 'Booking Fee' : isIncome ? 'Income' : 'Expense'}
                                    </Badge>
                                    {txn.category && (
                                        <Badge variant="outline" className="text-xs">{txn.category}</Badge>
                                    )}
                                    <p className="text-sm text-muted-foreground">{txn.date ? formatDate(txn.date) : ''}</p>
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className={`font-semibold ${isIncome ? 'text-green-600' : 'text-red-600'}`}>
                                {isIncome ? '+' : '-'}{formatCurrency(txn.amount || 0)}
                            </p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}